import { createPersistedJson } from '~/utils/persistedJson'

/**
 * Scoped, hierarchical UI-state memory backed by localStorage.
 *
 * Stores "the last <value> for a given <scope> within a <level>". A *level* is
 * an independent namespace (e.g. `company`, `leadAgency`, `planningDay`,
 * `filters`, `case`); within a level, each distinct *scope* string keeps its
 * own remembered value.
 *
 * This is the building block for the cascading "remember the last selection per
 * parent context" pattern: the caller builds each level's scope from its
 * resolved parents (e.g. `'C1|LA2|2026-06-10'`) and the memory recalls / saves
 * the value for exactly that scope. When there's no memory for a scope, the
 * caller falls back to that level's default.
 *
 * Storage shape: `{ [level]: { [scope]: value } }`. Values may be any
 * JSON-serialisable thing (an id string, a `planFilter` object, a filters map…).
 *
 * The persistence layer is abstracted here on purpose — a future server-backed
 * implementation only swaps `createPersistedJson` for a remote store, with the
 * same `recall` / `remember` surface, so no call site changes.
 *
 * @param {string} storageKey  localStorage key for this memory.
 */
export function createScopedMemory(storageKey) {
  const store = createPersistedJson(storageKey, {})

  function readAll() {
    const parsed = store.read()

    return parsed && typeof parsed === 'object' ? parsed : {}
  }

  /** The remembered value for (level, scope), or `null` when absent. */
  function recall(level, scope) {
    const byScope = readAll()[level]
    if (!byScope || typeof byScope !== 'object') return null
    const value = byScope[String(scope)]

    return value === undefined ? null : value
  }

  /** Save `value` as the last selection for (level, scope). */
  function remember(level, scope, value) {
    const all = readAll()

    const byScope =
      all[level] && typeof all[level] === 'object' ? all[level] : {}

    byScope[String(scope)] = value
    all[level] = byScope
    store.write(all)
  }

  /** Drop the remembered value for (level, scope). */
  function forget(level, scope) {
    const all = readAll()
    const byScope = all[level]
    if (byScope && typeof byScope === 'object' && String(scope) in byScope) {
      delete byScope[String(scope)]
      all[level] = byScope
      store.write(all)
    }
  }

  /** Wipe the whole memory (e.g. on logout). */
  function clear() {
    store.write({})
  }

  return { recall, remember, forget, clear }
}
